import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav, SiteFooter } from "@/components/SiteChrome";

export const Route = createFileRoute("/now")({
  head: () => ({
    meta: [
      { title: "Now — Akram Fortas" },
      {
        name: "description",
        content:
          "What Akram Fortas is working on, learning, reading, and thinking about right now. Inspired by Derek Sivers' /now page.",
      },
      { property: "og:title", content: "Now — Akram Fortas" },
      { property: "og:description", content: "A snapshot of where I am, this season." },
      { property: "og:url", content: "https://akramfortas.com/now" },
    ],
    links: [{ rel: "canonical", href: "https://akramfortas.com/now" }],
  }),
  component: NowPage,
});

const updated = "Spring 2026";

function NowPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteNav />

      <main className="container-prose pt-20 pb-16 md:pt-28">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
          Now — updated {updated}
        </p>
        <h1 className="mt-6 font-serif text-5xl text-balance text-foreground md:text-6xl">
          A snapshot of where I am, <span className="italic text-[color:var(--color-leaf)]">this season</span>.
        </h1>
        <p className="mt-6 max-w-2xl text-muted-foreground text-pretty">
          Inspired by{" "}
          <a className="link-quiet underline underline-offset-4" href="https://nownownow.com/about" target="_blank" rel="noreferrer noopener">
            Derek Sivers
          </a>
          . A page that gets rewritten more often than it gets read — and that's the point.
        </p>

        <div className="mt-16 space-y-12">
          <Block
            label="Working on"
            items={[
              "A small marketing studio rooted in honest positioning.",
              "Two productized experiments. Quiet launches, slow iterations.",
              "Rewriting my own site every few months until it feels like me.",
            ]}
          />
          <Block
            label="Learning"
            items={[
              "Systems thinking, via Donella Meadows.",
              "Better writing. Shorter sentences. Fewer adverbs.",
              "How to do less, on purpose.",
            ]}
          />
          <Block
            label="Reading"
            items={[
              "Thinking in Systems — Donella Meadows",
              "Four Thousand Weeks — Oliver Burkeman",
              "The Creative Act — Rick Rubin",
            ]}
          />
          <Block
            label="Where I am, mentally"
            items={[
              "Calmer than last year. Less reactive.",
              "Trying to confuse stillness with stagnation less often.",
              "Patient with the long game. Impatient with my own bullshit.",
            ]}
          />
        </div>

        <p className="mt-20 text-sm text-muted-foreground">
          ←{" "}
          <Link to="/" className="link-quiet underline underline-offset-4">
            Back to the garden
          </Link>
        </p>
      </main>

      <SiteFooter />
    </div>
  );
}

function Block({ label, items }: { label: string; items: string[] }) {
  return (
    <section className="grid gap-4 md:grid-cols-[10rem_1fr] md:gap-10">
      <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground pt-1">
        {label}
      </h2>
      <ul className="space-y-3">
        {items.map((it, i) => (
          <li key={i} className="font-serif text-2xl leading-snug text-foreground text-pretty">
            {it}
          </li>
        ))}
      </ul>
    </section>
  );
}
